import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Autocomplete,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  IconButton,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { getCollection, findDocumentsByField, updateDocument } from '../../services/firestoreService';

const EgresosYCirugias = () => {
  // --- Estado general ---
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // --- Datos de referencia ---
  const [patients, setPatients] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [procedures, setProcedures] = useState([]);

  // --- Lista de egresos pendientes ---
  const [egresos, setEgresos] = useState([]);

  // --- Vista: 'list' o 'form' ---
  const [view, setView] = useState('list');
  const [selectedEgreso, setSelectedEgreso] = useState(null);
  const [patientData, setPatientData] = useState(null);

  // --- Formulario de codificación ---
  const [formData, setFormData] = useState({
    diagnosticoPrincipal: null,
    diagnosticosSecundarios: [],
    procedimientoPrincipal: null,
    procedimientosSecundarios: [],
    condicionAlta: '',
    causaMuerte: '',
    complicaciones: false,
    tiposComplicaciones: [],
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [patientsData, egresosData, diagnosesData, proceduresData] = await Promise.all([
        getCollection('patients'),
        findDocumentsByField('egresosHospitalarios', 'codificado', false),
        getCollection('diagnoses'),
        getCollection('procedures'),
      ]);

      setPatients(patientsData);

      // Enriquecer egresos con datos de paciente
      const egresosEnriquecidos = egresosData.map((egreso) => {
        const patient = patientsData.find((p) => String(p.ficha) === String(egreso.ficha));
        return {
          ...egreso,
          patientName: patient?.name || '—',
          patientEspecie: patient?.species || '—',
          patientId: patient?.id || '',
        };
      });
      // Ordenar por fecha de ingreso desc
      egresosEnriquecidos.sort((a, b) => (b.fechaIngreso || '').localeCompare(a.fechaIngreso || ''));
      setEgresos(egresosEnriquecidos);

      // Ordenar diagnósticos y procedimientos por nombre
      setDiagnoses(diagnosesData.sort((a, b) => (a.diagnosis || '').localeCompare(b.diagnosis || '')));
      setProcedures(proceduresData.sort((a, b) => (a.procedure || '').localeCompare(b.procedure || '')));
    } catch (error) {
      console.error('Error cargando datos:', error);
      setSnackbar({ open: true, message: 'Error al cargar los datos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Acciones ---
  const handleOpenCodificar = (egreso) => {
    setSelectedEgreso(egreso);
    const patient = patients.find((p) => String(p.ficha) === String(egreso.ficha));
    setPatientData(patient || null);
    setFormData({
      diagnosticoPrincipal: null,
      diagnosticosSecundarios: [],
      procedimientoPrincipal: null,
      procedimientosSecundarios: [],
      condicionAlta: '',
      causaMuerte: '',
      complicaciones: false,
      tiposComplicaciones: [],
    });
    setView('form');
  };

  const handleBack = () => {
    setView('list');
    setSelectedEgreso(null);
    setPatientData(null);
  };

  const handleSave = async () => {
    if (!formData.diagnosticoPrincipal) {
      setSnackbar({ open: true, message: 'Selecciona al menos el Diagnóstico Principal', severity: 'warning' });
      return;
    }

    try {
      setSaving(true);

      const codificacionData = {
        codificado: true,
        codificadoAt: new Date(),
        diagnosticoPrincipal: {
          id: formData.diagnosticoPrincipal.id,
          name: formData.diagnosticoPrincipal.diagnosis || '',
          code: formData.diagnosticoPrincipal.code || '',
        },
        diagnosticosSecundarios: formData.diagnosticosSecundarios.map((d) => ({
          id: d.id,
          name: d.diagnosis || '',
          code: d.code || '',
        })),
        procedimientoPrincipal: formData.procedimientoPrincipal
          ? {
              id: formData.procedimientoPrincipal.id,
              name: formData.procedimientoPrincipal.procedure || '',
              code: formData.procedimientoPrincipal.code || '',
            }
          : null,
        procedimientosSecundarios: formData.procedimientosSecundarios.map((p) => ({
          id: p.id,
          name: p.procedure || '',
          code: p.code || '',
        })),
        patientId: selectedEgreso.patientId || '',
        patientName: selectedEgreso.patientName || '',
        condicionAlta: formData.condicionAlta || '',
        causaMuerte: formData.causaMuerte || '',
        complicaciones: formData.complicaciones || false,
        tiposComplicaciones: formData.tiposComplicaciones || [],
      };

      await updateDocument('egresosHospitalarios', selectedEgreso.id, codificacionData);

      setSnackbar({ open: true, message: 'Egreso codificado exitosamente', severity: 'success' });

      // Volver a la lista y recargar
      setView('list');
      setSelectedEgreso(null);
      setPatientData(null);
      await loadData();
    } catch (error) {
      console.error('Error guardando codificación:', error);
      setSnackbar({ open: true, message: 'Error al guardar la codificación', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // --- Render ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ===================== VISTA FORMULARIO =====================
  if (view === 'form' && selectedEgreso) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Volver a la lista
        </Button>
        <Typography variant="h6" gutterBottom>
          Codificar Egreso
        </Typography>
        {/* Datos generales del paciente */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Datos del Paciente
          </Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="text.secondary">Ficha</Typography>
              <Typography variant="body1">{selectedEgreso.ficha}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="text.secondary">Nombre</Typography>
              <Typography variant="body1">{selectedEgreso.patientName}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="text.secondary">Especie</Typography>
              <Typography variant="body1">{patientData?.species || '—'}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="text.secondary">Raza</Typography>
              <Typography variant="body1">{patientData?.breed || '—'}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="text.secondary">Edad</Typography>
              <Typography variant="body1">{patientData?.edad || '—'}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="text.secondary">Fecha Ingreso</Typography>
              <Typography variant="body1">{selectedEgreso.fechaIngreso}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="text.secondary">Fecha Alta</Typography>
              <Typography variant="body1">{selectedEgreso.fechaAlta}</Typography>
            </Grid>
          </Grid>
        </Paper>
        {/* Sección: Diagnósticos */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Diagnósticos
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Autocomplete
              value={formData.diagnosticoPrincipal}
              onChange={(e, newVal) => setFormData({ ...formData, diagnosticoPrincipal: newVal })}
              options={diagnoses}
              getOptionLabel={(opt) => opt.diagnosis || ''}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter((o) =>
                  (o.diagnosis || '').toLowerCase().includes(q) ||
                  (o.code || '').toLowerCase().includes(q)
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.diagnosis}</Typography>
                    {option.code && (
                      <Typography variant="caption" color="text.secondary">Código: {option.code}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Diagnóstico Principal" placeholder="Buscar por nombre o código..." required fullWidth />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              noOptionsText="No se encontraron diagnósticos"
              fullWidth
            />

            {formData.diagnosticoPrincipal && (
              <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
                <Typography variant="caption" color="text.secondary">Diagnóstico Principal seleccionado:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {formData.diagnosticoPrincipal.diagnosis}
                  {formData.diagnosticoPrincipal.code && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({formData.diagnosticoPrincipal.code})
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}

            <Divider />

            <Autocomplete
              multiple
              value={formData.diagnosticosSecundarios}
              onChange={(e, newVal) => setFormData({ ...formData, diagnosticosSecundarios: newVal })}
              options={diagnoses}
              getOptionLabel={(opt) => opt.diagnosis || ''}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter((o) =>
                  (o.diagnosis || '').toLowerCase().includes(q) ||
                  (o.code || '').toLowerCase().includes(q)
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.diagnosis}</Typography>
                    {option.code && (
                      <Typography variant="caption" color="text.secondary">Código: {option.code}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Diagnósticos Secundarios" placeholder="Buscar por nombre o código..." fullWidth />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              noOptionsText="No se encontraron diagnósticos"
              fullWidth
            />

            {formData.diagnosticosSecundarios.length > 0 && (
              <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'grey.400' }}>
                <Typography variant="caption" color="text.secondary">Diagnósticos Secundarios seleccionados:</Typography>
                {formData.diagnosticosSecundarios.map((d, idx) => (
                  <Typography key={d.id} variant="body2">
                    {idx + 1}. {d.diagnosis}
                    {d.code && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({d.code})
                      </Typography>
                    )}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
        {/* Sección: Procedimientos */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Procedimientos
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Autocomplete
              value={formData.procedimientoPrincipal}
              onChange={(e, newVal) => setFormData({ ...formData, procedimientoPrincipal: newVal })}
              options={procedures}
              getOptionLabel={(opt) => opt.procedure || ''}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter((o) =>
                  (o.procedure || '').toLowerCase().includes(q) ||
                  (o.code || '').toLowerCase().includes(q)
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.procedure}</Typography>
                    {option.code && (
                      <Typography variant="caption" color="text.secondary">Código: {option.code}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Procedimiento Principal" placeholder="Buscar por nombre o código..." fullWidth />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              noOptionsText="No se encontraron procedimientos"
              fullWidth
            />

            {formData.procedimientoPrincipal && (
              <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
                <Typography variant="caption" color="text.secondary">Procedimiento Principal seleccionado:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {formData.procedimientoPrincipal.procedure}
                  {formData.procedimientoPrincipal.code && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({formData.procedimientoPrincipal.code})
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}

            <Divider />

            <Autocomplete
              multiple
              value={formData.procedimientosSecundarios}
              onChange={(e, newVal) => setFormData({ ...formData, procedimientosSecundarios: newVal })}
              options={procedures}
              getOptionLabel={(opt) => opt.procedure || ''}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter((o) =>
                  (o.procedure || '').toLowerCase().includes(q) ||
                  (o.code || '').toLowerCase().includes(q)
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.procedure}</Typography>
                    {option.code && (
                      <Typography variant="caption" color="text.secondary">Código: {option.code}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Procedimientos Secundarios" placeholder="Buscar por nombre o código..." fullWidth />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              noOptionsText="No se encontraron procedimientos"
              fullWidth
            />

            {formData.procedimientosSecundarios.length > 0 && (
              <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'grey.400' }}>
                <Typography variant="caption" color="text.secondary">Procedimientos Secundarios seleccionados:</Typography>
                {formData.procedimientosSecundarios.map((p, idx) => (
                  <Typography key={p.id} variant="body2">
                    {idx + 1}. {p.procedure}
                    {p.code && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({p.code})
                      </Typography>
                    )}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
        {/* Sección: Datos de Egreso */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Datos de Egreso
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Condición de Alta */}
            <FormControl fullWidth>
              <InputLabel id="condicion-alta-label">Condición de Alta</InputLabel>
              <Select
                labelId="condicion-alta-label"
                value={formData.condicionAlta}
                label="Condición de Alta"
                onChange={(e) => setFormData({
                  ...formData,
                  condicionAlta: e.target.value,
                  causaMuerte: (e.target.value !== 'Fallecimiento' && e.target.value !== 'Eutanasia') ? '' : formData.causaMuerte,
                })}
              >
                <MenuItem value="">— Seleccionar —</MenuItem>
                <MenuItem value="Alta a Domicilio">Alta a Domicilio</MenuItem>
                <MenuItem value="Derivación a otro Centro">Derivación a otro Centro</MenuItem>
                <MenuItem value="Fallecimiento">Fallecimiento</MenuItem>
                <MenuItem value="Eutanasia">Eutanasia</MenuItem>
                <MenuItem value="Alta Voluntaria del Propietario">Alta Voluntaria del Propietario</MenuItem>
              </Select>
            </FormControl>

            {/* Causa de Muerte / Eutanasia — solo visible si aplica */}
            {(formData.condicionAlta === 'Fallecimiento' || formData.condicionAlta === 'Eutanasia') && (
              <FormControl fullWidth>
                <InputLabel id="causa-muerte-label">Causa de Muerte / Eutanasia</InputLabel>
                <Select
                  labelId="causa-muerte-label"
                  value={formData.causaMuerte}
                  label="Causa de Muerte / Eutanasia"
                  onChange={(e) => setFormData({ ...formData, causaMuerte: e.target.value })}
                >
                  <MenuItem value="">— Seleccionar —</MenuItem>
                  <MenuItem value="Falla multiorgánica">Falla multiorgánica</MenuItem>
                  <MenuItem value="Shock séptico">Shock séptico</MenuItem>
                  <MenuItem value="Insuficiencia renal aguda">Insuficiencia renal aguda</MenuItem>
                  <MenuItem value="Insuficiencia hepática">Insuficiencia hepática</MenuItem>
                  <MenuItem value="Insuficiencia cardíaca">Insuficiencia cardíaca</MenuItem>
                  <MenuItem value="Insuficiencia respiratoria">Insuficiencia respiratoria</MenuItem>
                  <MenuItem value="Paro cardiorrespiratorio">Paro cardiorrespiratorio</MenuItem>
                  <MenuItem value="Coagulación intravascular diseminada (CID)">Coagulación intravascular diseminada (CID)</MenuItem>
                  <MenuItem value="Trauma severo">Trauma severo</MenuItem>
                  <MenuItem value="Hemorragia interna">Hemorragia interna</MenuItem>
                  <MenuItem value="Neoplasia avanzada / metástasis">Neoplasia avanzada / metástasis</MenuItem>
                  <MenuItem value="Enfermedad neurológica grave">Enfermedad neurológica grave</MenuItem>
                  <MenuItem value="Pancreatitis necrotizante">Pancreatitis necrotizante</MenuItem>
                  <MenuItem value="Obstrucción intestinal / vólvulo">Obstrucción intestinal / vólvulo</MenuItem>
                  <MenuItem value="Peritonitis séptica">Peritonitis séptica</MenuItem>
                  <MenuItem value="Enfermedad infecciosa sistémica">Enfermedad infecciosa sistémica</MenuItem>
                  <MenuItem value="Complicación anestésica">Complicación anestésica</MenuItem>
                  <MenuItem value="Complicación post-quirúrgica">Complicación post-quirúrgica</MenuItem>
                  <MenuItem value="Calidad de vida irrecuperable">Calidad de vida irrecuperable</MenuItem>
                  <MenuItem value="Otra">Otra</MenuItem>
                </Select>
              </FormControl>
            )}

            <Divider />

            {/* Complicaciones Intrahospitalarias */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.complicaciones}
                    onChange={(e) => setFormData({
                      ...formData,
                      complicaciones: e.target.checked,
                      tiposComplicaciones: e.target.checked ? formData.tiposComplicaciones : [],
                    })}
                  />
                }
                label="Complicaciones Intrahospitalarias"
              />

              {formData.complicaciones && (
                <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tiposComplicaciones.includes('Infecciones nosocomiales')}
                        onChange={(e) => {
                          const val = 'Infecciones nosocomiales';
                          setFormData({
                            ...formData,
                            tiposComplicaciones: e.target.checked
                              ? [...formData.tiposComplicaciones, val]
                              : formData.tiposComplicaciones.filter((c) => c !== val),
                          });
                        }}
                      />
                    }
                    label="Infecciones nosocomiales"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tiposComplicaciones.includes('Reacciones adversas a medicamentos')}
                        onChange={(e) => {
                          const val = 'Reacciones adversas a medicamentos';
                          setFormData({
                            ...formData,
                            tiposComplicaciones: e.target.checked
                              ? [...formData.tiposComplicaciones, val]
                              : formData.tiposComplicaciones.filter((c) => c !== val),
                          });
                        }}
                      />
                    }
                    label="Reacciones adversas a medicamentos"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tiposComplicaciones.includes('Dehiscencia de herida')}
                        onChange={(e) => {
                          const val = 'Dehiscencia de herida';
                          setFormData({
                            ...formData,
                            tiposComplicaciones: e.target.checked
                              ? [...formData.tiposComplicaciones, val]
                              : formData.tiposComplicaciones.filter((c) => c !== val),
                          });
                        }}
                      />
                    }
                    label="Dehiscencia de herida"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tiposComplicaciones.includes('Complicaciones Anestésicas/Quirúrgicas')}
                        onChange={(e) => {
                          const val = 'Complicaciones Anestésicas/Quirúrgicas';
                          setFormData({
                            ...formData,
                            tiposComplicaciones: e.target.checked
                              ? [...formData.tiposComplicaciones, val]
                              : formData.tiposComplicaciones.filter((c) => c !== val),
                          });
                        }}
                      />
                    }
                    label="Complicaciones Anestésicas/Quirúrgicas"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tiposComplicaciones.includes('Desequilibrios Metabólicos/Electrolíticos')}
                        onChange={(e) => {
                          const val = 'Desequilibrios Metabólicos/Electrolíticos';
                          setFormData({
                            ...formData,
                            tiposComplicaciones: e.target.checked
                              ? [...formData.tiposComplicaciones, val]
                              : formData.tiposComplicaciones.filter((c) => c !== val),
                          });
                        }}
                      />
                    }
                    label="Desequilibrios Metabólicos/Electrolíticos"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tiposComplicaciones.includes('Lesiones Físicas/Manejo')}
                        onChange={(e) => {
                          const val = 'Lesiones Físicas/Manejo';
                          setFormData({
                            ...formData,
                            tiposComplicaciones: e.target.checked
                              ? [...formData.tiposComplicaciones, val]
                              : formData.tiposComplicaciones.filter((c) => c !== val),
                          });
                        }}
                      />
                    }
                    label="Lesiones Físicas/Manejo"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleBack} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !formData.diagnosticoPrincipal}
          >
            {saving ? 'Guardando...' : 'Guardar Codificación'}
          </Button>
        </Box>
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ===================== VISTA LISTA =====================
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Egresos Pendientes de Codificación
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Selecciona un egreso para codificarlo con diagnósticos y procedimientos.
      </Typography>

      {egresos.length === 0 ? (
        <Alert severity="info">No hay egresos pendientes de codificación.</Alert>
      ) : (
        <>
          <Chip label={`${egresos.length} pendiente(s)`} color="warning" size="small" sx={{ mb: 2 }} />
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell><strong>Ficha</strong></TableCell>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell><strong>Especie</strong></TableCell>
                  <TableCell><strong>Fecha Ingreso</strong></TableCell>
                  <TableCell><strong>Fecha Alta</strong></TableCell>
                  <TableCell align="center"><strong>Acción</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {egresos.map((egreso) => (
                  <TableRow key={egreso.id} hover>
                    <TableCell>{egreso.ficha}</TableCell>
                    <TableCell>{egreso.patientName}</TableCell>
                    <TableCell>{egreso.patientEspecie}</TableCell>
                    <TableCell>{egreso.fechaIngreso}</TableCell>
                    <TableCell>{egreso.fechaAlta}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleOpenCodificar(egreso)} title="Codificar">
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EgresosYCirugias;
